import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./context/AuthContext";
import { useAppTranslation } from "./components/TranslationContext";

// 👇 Компонент-анимация логотипа
const HeroLogo = ({ onFinish }) => {
  const [step, setStep] = useState(0); // 0 = скрыто, 1 = буквы, 2 = подзаголовок, 3 = подъем
  const [fadingOut, setFadingOut] = useState(false);
  const containerRef = useRef(null);

  const { i18n } = useTranslation();
  const { t, setLang, lang } = useAppTranslation();

  useEffect(() => {
    let timers = [];

    // 1) Буквы поочередно
    timers.push(setTimeout(() => setStep(1), 300));

    // 2) Подзаголовок
    timers.push(setTimeout(() => setStep(2), 2000));

    // 3) Подъем логотипа
    timers.push(setTimeout(() => setStep(3), 3500));

    // 4) Завершение
    timers.push(
        setTimeout(() => {
          setFadingOut(true);
          setTimeout(() => onFinish(), 1000); // ждём 1с пока фон исчезнет
        }, 5000)
      );

    return () => timers.forEach((t) => clearTimeout(t));
  }, [onFinish]);

  return (
    <div
      ref={containerRef}
      className={`hero-logo-container ${fadingOut ? "fade-out" : ""}`}
    >
      <div
        className={`hero-logo ${step >= 3 ? "move-to-header" : ""}`}
        style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <h1 className={`hero-logo-title ${step >= 3 ? "to-white" : ""}`}>
          {t('logo').split("").map((ch, i) => (
            <span
              key={i}
              className={`logo-letter ${step >= 1 ? "show" : ""}`}
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              {ch}
            </span>
          ))}
        </h1>
        <p
          className={`hero-logo-subtitle ${step >= 2 ? "show" : ""} ${
            step >= 3 ? "to-white" : ""
          }`}
        >
          {t('logoSub')}
        </p>
      </div>
    </div>
  );
};

// 👇 Навигация
export const Navigation = () => {
  const { user, logout, isAuthenticated, language, setLanguage } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  
  
  const navigate = useNavigate();
  const menuRef = useRef();

  const location = useLocation();
  const isHome = (location.pathname === "/") || (location.pathname === "/about");
  const [avatarUrl, setAvatarUrl] = useState("/images/user.png");
  
  const [showHero, setShowHero] = useState(() => isHome);

  const { i18n } = useTranslation();
  const { t, setLang, lang } = useAppTranslation();
  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language);
      setLang(language);
    }
  }, [language, i18n, setLang]);

  const changeLanguage = (newLang) => {
    i18n.changeLanguage(newLang);
    setLanguage(newLang);
    setLang(newLang);
    localStorage.setItem("language", newLang);
  };

  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.id) {
        try {
          const res = await fetch(
            `https://api.eduskill.uz/api/custom-users/?user=${user.id}`
          );
          const data = await res.json();
          if (data.length > 0 && data[0].img) {
            setAvatarUrl(data[0].img);
          }
        } catch (err) {
          console.error("Ошибка загрузки аватара:", err);
        }
      }
    };
    fetchAvatar();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <>
      {isHome && showHero && <HeroLogo onFinish={() => setShowHero(false)} />}

      <nav className={`nav ${isHome ? "nav--home" : "navBGwhite"}`}>
        <div className="links nav__links">
          <NavLink
            to="/courses"
            className={({ isActive }) => `link ${isActive ? "active" : ""}`}
          >
            {t("menuCourses")}
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) => `link ${isActive ? "active" : ""}`}
          >
            {t("menuAbout")}
          </NavLink>
        </div>

        <div className="nav__left">
          <Link className="logo" to="/">
            <span>{t('logo')}</span>
            <p>{t('logoSub')}</p>
          </Link>
        </div>

        <div className="nav__info">
          <div className="login lang">
            <button
              className={`link navLink__lang ${
                lang === "ru" ? "active-lang" : ""
              }`}
              onClick={() => changeLanguage("ru")}
            >
              Ru
            </button>
            <button
              className={`link navLink__lang ${
                lang === "en" ? "active-lang" : ""
              }`}
              onClick={() => changeLanguage("en")}
            >
              En
            </button>
            <button
              className={`link navLink__lang ${
                lang === "uz" ? "active-lang" : ""
              }`}
              onClick={() => changeLanguage("uz")}
            >
              Uz
            </button>
          </div>

          <div className="login">
            {isAuthenticated ? (
              <div className="avatarWrapper" style={styles.avatarWrapper}>
                <img
                  src={avatarUrl}
                  alt="User"
                  className="profile__avatar"
                  onClick={() => setMenuOpen(!menuOpen)}
                />
                {menuOpen && (
                  <div className="profile__menu" ref={menuRef}>
                    {user && (
                      <div className="profile__info">
                        <p
                          style={{ fontWeight: "bold", marginBottom: "5px" }}
                        >
                          {user.username}
                        </p>
                        <p style={{ fontSize: "12px", color: "#777" }}>
                          {user.email}
                        </p>
                      </div>
                    )}
                    <NavLink
                      to="/profile"
                      className="link"
                      onClick={() => setMenuOpen(false)}
                    >
                      <img
                        src="https://cdn-icons-png.flaticon.com/128/9308/9308015.png"
                        alt="Профиль"
                      />
                      <p>{t("profile")}</p>
                    </NavLink>
                    <Link to="/" className="link" onClick={handleLogout}>
                      <img
                        src="https://cdn-icons-png.flaticon.com/128/1286/1286853.png"
                        alt="Выйти"
                      />
                      <p>{t("logout")}</p>
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="link">
                {t("login")}
              </Link>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

const styles = {
  avatarWrapper: { position: "relative" },
};
